import { Controller } from '@hotwired/stimulus'

// Ensure we only have one event listener for all the popovers
class GlobalEventsManager {
  static turboAfterStreamRender = false

  static addTurboAfterStreamRenderListener(callback) {
    if (!GlobalEventsManager.turboAfterStreamRender) {
      document.addEventListener('turbo:after-stream-render', callback)
      GlobalEventsManager.turboAfterStreamRender = true
    }
  }
}

/** Flowbite's Popover always positions itself with Popper's default `absolute` strategy, which
 * stays subject to a scrollable ancestor's overflow clipping (e.g. our tables use
 * `overflow-y-auto`) even once Popper computes "correct" coordinates — only `fixed` positioning
 * (relative to the viewport) escapes that.
 *
 * We can't patch this by importing Flowbite's `Popover` class and overriding its prototype: the
 * app loads the pre-bundled `flowbite/dist/flowbite.turbo.js`, which ships its own self-contained
 * copy of that class in a separate module graph, so patching the `flowbite` package's own export
 * never touches the instances actually created by `window.initPopovers()`.
 *
 * Instead, react at click-time through Flowbite's documented `window.FlowbiteInstances` registry
 * and force the already-created instance's underlying Popper to the `fixed` strategy. We listen
 * during the capture phase so this always runs before Flowbite's own (bubble-phase) click handler
 * shows the popover, regardless of script load order. **/
if (typeof document !== 'undefined' && !document.usesFixedStrategyPopoverPatch) {
  document.addEventListener(
    'click',
    (event) => {
      const triggerEl = event.target.closest('[data-popover-target]')
      if (!triggerEl) return

      const popoverId = triggerEl.getAttribute('data-popover-target')
      if (!window.FlowbiteInstances?.instanceExists('Popover', popoverId)) return

      const instance = window.FlowbiteInstances.getInstance('Popover', popoverId)
      instance._popperInstance?.setOptions({ strategy: 'fixed' })
    },
    true
  )
  document.usesFixedStrategyPopoverPatch = true
}

export default class extends Controller {
  connect() {
    /** This is a patch to reinitialize the popovers/tooltips after the flowbite custom turbo event kicks off. This happens for example on the
         clinicians/<uuid>/pay_stubs' page where the popovers don't render after updating the pay period.
         https://github.com/themesberg/flowbite/blob/7941f8bb79040c395da8b985329c633e29bb3064/src/index.turbo.ts#L20
         https://github.com/themesberg/flowbite/issues/796 **/
    GlobalEventsManager.addTurboAfterStreamRenderListener(window.initPopovers)
  }
}
